import type { HTMLAttributes, ReactNode } from 'react';
import { cn } from '@/lib/cn';

/**
 * Card.
 *
 * §11: "separation by 1px #DCE3EE borders, not shadows." A page of medicines is
 * a dense list of things a buyer compares; forty drop shadows turn comparison
 * into visual noise, while a hairline border keeps every card at the same
 * optical depth.
 */

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  /** Removes the internal padding, for cards with their own header band. */
  flush?: boolean;
  /** Full-bleed on mobile: no side borders, no radius. */
  bleed?: boolean;
  children: ReactNode;
}

export function Card({ flush = false, bleed = false, className, children, ...props }: CardProps) {
  return (
    <div
      className={cn(
        'border border-line bg-white',
        bleed ? 'rounded-none border-x-0' : 'rounded-card',
        flush ? 'overflow-hidden' : 'p-[14px]',
        className,
      )}
      {...props}
    >
      {children}
    </div>
  );
}

/** The tinted band at the top of a grouped card — a pharmacy header in a cart. */
export function CardBand({ className, children }: { className?: string; children: ReactNode }) {
  return (
    <div
      className={cn(
        'flex items-center gap-2.5 border-b border-line-2 bg-blue-tint-2 px-[14px] py-3',
        className,
      )}
    >
      {children}
    </div>
  );
}

/** A section heading with an optional trailing action. */
export function SectionHeader({
  title,
  action,
  className,
}: {
  title: ReactNode;
  action?: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('mb-2.5 flex items-baseline justify-between gap-2.5', className)}>
      <h2 className="m-0 font-display text-[14.5px] font-bold tracking-[-0.01em]">{title}</h2>
      {action}
    </div>
  );
}
